import { SelectionRange } from "@progress/kendo-react-dateinputs";
import { CompletedJobDocumentType } from "../../../../lib/supabase/supabaseTypes";
import { RecipientTypeEnum } from "./types";
import {
	RecipientTableRow,
	JobDocumentTableRow,
	CheckboxState,
} from "./sammelrechnungen-table.types";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { getGuarantorDisplayName } from "../../lib/utils/guarantor-utils";
import {
	ClientEntityType,
	GuarantorEntityType,
	PatientEntityType,
	PractitionerEntityType,
} from "../../../../lib/supabase/supabaseTypes";
import { Currency } from "../../types/enums";

/**
 * Resolves the display currency for a set of job documents. The invoice currency
 * comes from job_currency_text; a missing value means CHF.
 */
export const getCurrencyForJobs = (
	jobDocuments: { job_currency_text?: string | null }[]
): string => {
	const currency = jobDocuments.find((job) => job.job_currency_text)
		?.job_currency_text;
	return currency || Currency.CHF;
};

export interface AmountByCurrency {
	currency: string;
	amount: number;
}

/**
 * Sums the amounts of a set of job documents bucketed by currency
 * (job_currency_text, defaulting to CHF). Amounts in different currencies can't
 * be added together, so each currency keeps its own total. Sorted by currency.
 */
export const sumAmountsByCurrency = (
	jobDocuments: {
		job_currency_text?: string | null;
		amount?: number | null;
	}[]
): AmountByCurrency[] => {
	const byCurrency = new Map<string, number>();
	jobDocuments.forEach((job) => {
		const currency = job.job_currency_text || Currency.CHF;
		byCurrency.set(
			currency,
			(byCurrency.get(currency) ?? 0) + (job.amount || 0)
		);
	});
	return Array.from(byCurrency.entries())
		.map(([currency, amount]) => ({ currency, amount }))
		.sort((a, b) => a.currency.localeCompare(b.currency));
};

/**
 * Subtracts a single akonto balance from per-currency amounts, consuming the
 * balance across currencies in order (exact in the normal single-currency case).
 */
export const applyAkontoByCurrency = (
	amountsByCurrency: AmountByCurrency[],
	akonto: number
): AmountByCurrency[] => {
	let remaining = Math.max(akonto, 0);
	return amountsByCurrency.map(({ currency, amount }) => {
		const deduction = Math.min(remaining, Math.max(amount, 0));
		remaining -= deduction;
		return { currency, amount: amount - deduction };
	});
};

/**
 * Checks if an array of values has conflicts (more than one unique value)
 * Treats null and undefined as the same "empty" value
 */
export const hasConflict = (
	values: any[],
	interpretNullAs?: string
): boolean => {
	// Normalize null and undefined to a single representation
	let normalizedValues = values.map((v) =>
		v === null || v === undefined ? null : v
	);
	if (interpretNullAs) {
		normalizedValues = normalizedValues.map((v) =>
			v === null ? interpretNullAs : v
		);
	}
	const uniqueValues = new Set(normalizedValues);
	return uniqueValues.size > 1;
};

interface RecipientGroup {
	recipientType: RecipientTypeEnum;
	recipientId: string;
	clientId: string;
	jobDocuments: CompletedJobDocumentType[];
}

export const isInDateRange = (
	jobDocument: CompletedJobDocumentType,
	dateRange: SelectionRange
) => {
	const documentDate = new Date(jobDocument.date as string);
	const startDate = new Date(dateRange.start as Date);
	const endDate = new Date(dateRange.end as Date);

	documentDate.setHours(0, 0, 0, 0);
	startDate.setHours(0, 0, 0, 0);
	endDate.setHours(0, 0, 0, 0);

	return documentDate >= startDate && documentDate <= endDate;
};

export const sortJobDocuments = (
	jobDocuments: JobDocumentTableRow[],
	sortColumn: string | null,
	sortDirection: "asc" | "desc",
	patientsLookup?: Record<string, PatientEntityType | any>,
	practitionersLookup?: Record<number, PractitionerEntityType>,
	clientsLookup?: Record<string, ClientEntityType>
): JobDocumentTableRow[] => {
	if (!sortColumn) return jobDocuments;

	const sorted = [...jobDocuments].sort((a, b) => {
		let aValue: any;
		let bValue: any;

		switch (sortColumn) {
			case "date":
				aValue = a.date ? new Date(a.date as string).getTime() : 0;
				bValue = b.date ? new Date(b.date as string).getTime() : 0;
				break;
			case "patient":
				if (patientsLookup) {
					const aPatient = a.job_patient_id
						? patientsLookup[a.job_patient_id]
						: null;
					const bPatient = b.job_patient_id
						? patientsLookup[b.job_patient_id]
						: null;
					aValue = aPatient?.last_name
						? aPatient.last_name.toLowerCase()
						: "";
					bValue = bPatient?.last_name
						? bPatient.last_name.toLowerCase()
						: "";
				} else {
					aValue = (a.job_patient_id || "").toLowerCase();
					bValue = (b.job_patient_id || "").toLowerCase();
				}
				break;
			case "job_code":
				aValue = (a.job_code || "").toLowerCase();
				bValue = (b.job_code || "").toLowerCase();
				break;
			case "job_title":
				aValue = (a.job_title || "").toLowerCase();
				bValue = (b.job_title || "").toLowerCase();
				break;
			case "type":
				aValue = (a.type || "").toLowerCase();
				bValue = (b.type || "").toLowerCase();
				break;
			case "practitioner":
				if (practitionersLookup) {
					const aPractitioner = a.job_practitioner_id
						? practitionersLookup[a.job_practitioner_id]
						: null;
					const bPractitioner = b.job_practitioner_id
						? practitionersLookup[b.job_practitioner_id]
						: null;
					aValue = aPractitioner
						? getClientDisplayName(
								aPractitioner as unknown as ClientEntityType
							).toLowerCase()
						: "";
					bValue = bPractitioner
						? getClientDisplayName(
								bPractitioner as unknown as ClientEntityType
							).toLowerCase()
						: "";
				} else {
					aValue = a.job_practitioner_id || 0;
					bValue = b.job_practitioner_id || 0;
				}
				break;
			case "client":
				if (clientsLookup) {
					const aClient = a.client_id
						? clientsLookup[a.client_id]
						: null;
					const bClient = b.client_id
						? clientsLookup[b.client_id]
						: null;
					aValue = aClient
						? getClientDisplayName(aClient).toLowerCase()
						: "";
					bValue = bClient
						? getClientDisplayName(bClient).toLowerCase()
						: "";
				} else {
					aValue = (a.client_id || "").toLowerCase();
					bValue = (b.client_id || "").toLowerCase();
				}
				break;
			case "amount":
				aValue = a.amount || 0;
				bValue = b.amount || 0;
				break;
			case "monthly_discount":
				aValue = a.is_ignore_monthly_discount ? 1 : 0;
				bValue = b.is_ignore_monthly_discount ? 1 : 0;
				break;
			case "tax_rate":
				aValue = a.job_tax_rate || 0;
				bValue = b.job_tax_rate || 0;
				break;
			case "mwst_number":
				aValue = (a.job_mwst_number || "").toLowerCase();
				bValue = (b.job_mwst_number || "").toLowerCase();
				break;
			case "mwst_type":
				aValue = a.job_is_mwst_eust ? 1 : 0;
				bValue = b.job_is_mwst_eust ? 1 : 0;
				break;
			case "round_to":
				aValue = a.job_round_to || 0;
				bValue = b.job_round_to || 0;
				break;
			case "currency":
				aValue = (a.job_currency_text || "").toLowerCase();
				bValue = (b.job_currency_text || "").toLowerCase();
				break;
			default:
				return 0;
		}

		if (aValue === bValue) return 0;

		const comparison = aValue < bValue ? -1 : 1;
		return sortDirection === "asc" ? comparison : -comparison;
	});

	return sorted;
};

export const transformToTableData = (
	recipientGroups: RecipientGroup[],
	selectedJobDocumentIds: Record<string, Set<number>>,
	clientsLookup: Record<string, ClientEntityType>,
	guarantorLookup: Record<string, GuarantorEntityType>,
	patientsLookup?: Record<string, PatientEntityType | any>,
	practitionersLookup?: Record<number, PractitionerEntityType>
): RecipientTableRow[] => {
	return recipientGroups.map((group): RecipientTableRow => {
		const recipientKey = `${group.clientId}_${group.recipientId}`;
		const selectedIds =
			selectedJobDocumentIds[recipientKey] || new Set<number>();

		// Get recipient name (lastName, firstName format for proper sorting)
		let recipientName = "";
		if (group.recipientType === RecipientTypeEnum.CLIENT) {
			const client = clientsLookup[group.recipientId];
			recipientName = client
				? getClientDisplayName(client, true)
				: "Unbekannter Kunde";
		} else {
			const guarantor = guarantorLookup[group.recipientId];
			recipientName = guarantor
				? getGuarantorDisplayName(guarantor, true)
				: "Unbekannter Garant";
		}

		// Transform job documents to table rows
		const subRows: JobDocumentTableRow[] = group.jobDocuments.map(
			(jobDoc): JobDocumentTableRow => ({
				...jobDoc,
				tableRowType: "jobDocument",
				recipientId: group.recipientId,
				recipientType: group.recipientType,
				clientId: group.clientId,
			})
		);

		const sortedSubRows = sortJobDocuments(
			subRows,
			"date",
			"desc",
			patientsLookup,
			practitionersLookup,
			clientsLookup
		);

		// Calculate totals
		const totalAmount = group.jobDocuments.reduce(
			(sum, job) => sum + (job.amount || 0),
			0
		);
		const allJobDocumentIds = new Set(
			group.jobDocuments.map((job) => job.id as number)
		);

		// Calculate conflict information for all fields using centralized function
		const hasMixedVAT = hasConflict(
			group.jobDocuments.map((job) => job.job_tax_rate)
		);
		const hasMixedMwstNumber = hasConflict(
			group.jobDocuments.map((job) => job.job_mwst_number)
		);
		const hasMixedMwstType = hasConflict(
			group.jobDocuments.map((job) => job.job_is_mwst_eust)
		);
		const hasMixedRounding = hasConflict(
			group.jobDocuments.map((job) => job.job_round_to)
		);
		const hasMixedCurrency = hasConflict(
			group.jobDocuments.map((job) => job.job_currency_text),
			"CHF"
		);

		// Keep the Sets for display purposes (to show the actual values)
		const vatRates = new Set<number>();
		const mwstNumbers = new Set<string>();
		const mwstTypes = new Set<boolean>();
		const roundingModes = new Set<number>();
		const currencies = new Set<string>();

		group.jobDocuments.forEach((job) => {
			const taxRate = job.job_tax_rate;
			const mwstNumber = job.job_mwst_number;
			const mwstType = job.job_is_mwst_eust;
			const roundTo = job.job_round_to;
			const currency = job.job_currency_text;

			if (taxRate !== null && taxRate !== undefined)
				vatRates.add(taxRate);
			if (mwstNumber !== null && mwstNumber !== undefined)
				mwstNumbers.add(mwstNumber);
			if (mwstType !== null && mwstType !== undefined)
				mwstTypes.add(mwstType);
			if (roundTo !== null && roundTo !== undefined)
				roundingModes.add(roundTo);
			if (currency !== null && currency !== undefined)
				currencies.add(currency);
		});

		return {
			tableRowType: "recipient",
			recipientId: group.recipientId,
			recipientType: group.recipientType,
			recipientName,
			totalAmount,
			jobDocumentCount: group.jobDocuments.length,
			clientId: group.clientId,
			subRows: sortedSubRows,
			selectedJobDocumentIds: selectedIds,
			allJobDocumentIds,
			hasMixedVAT,
			vatRates,
			hasMixedMwstNumber,
			mwstNumbers,
			hasMixedMwstType,
			mwstTypes,
			hasMixedRounding,
			roundingModes,
			hasMixedCurrency,
			currencies,
			sortColumn: "date",
			sortDirection: "desc",
		};
	});
};

export const getCheckboxState = (
	recipientRow: RecipientTableRow,
	dateRange: SelectionRange
): CheckboxState => {
	const { selectedJobDocumentIds, allJobDocumentIds, subRows } = recipientRow;

	// Get job documents that are in date range
	const jobDocsInDateRange = subRows.filter((job) =>
		isInDateRange(job, dateRange)
	);
	const inDateRangeIds = new Set(
		jobDocsInDateRange.map((job) => job.id as number)
	);

	// Count selections
	const totalJobs = allJobDocumentIds.size;
	const selectedCount = selectedJobDocumentIds.size;
	const inDateRangeCount = inDateRangeIds.size;

	// Determine state
	if (selectedCount === 0) {
		return CheckboxState.EMPTY;
	}

	if (selectedCount === totalJobs) {
		return CheckboxState.FULL;
	}

	// Check if all selected items are exactly the ones in date range
	const selectedInDateRange = Array.from(selectedJobDocumentIds).filter(
		(id) => inDateRangeIds.has(id)
	);
	if (
		selectedInDateRange.length === inDateRangeCount &&
		selectedCount === inDateRangeCount
	) {
		return CheckboxState.DATE_FILTERED;
	}

	return CheckboxState.PARTIAL;
};

export const calculateSelectedAmount = (
	recipientRow: RecipientTableRow
): number => {
	return recipientRow.subRows
		.filter((job) =>
			recipientRow.selectedJobDocumentIds.has(job.id as number)
		)
		.reduce((sum, job) => sum + (job.amount || 0), 0);
};

export const getVisibleColumnsForRecipient = (
	recipientRow: RecipientTableRow
) => {
	const columns = {
		date: true, // Always show
		patient: recipientRow.subRows.some((job) => job.job_patient_id),
		job: true, // Always show
		practitioner: recipientRow.subRows.some(
			(job) => job.job_practitioner_id
		),
		client: recipientRow.recipientType === RecipientTypeEnum.GUARANTOR,
		type: true, // Always show
		monthlyDiscount: recipientRow.subRows.some(
			(job) => job.is_ignore_monthly_discount
		),
		amount: true, // Always show
	};

	return columns;
};

export const getOptimalColumnWidths = (
	tableData: RecipientTableRow[]
): {
	date: string;
	patient?: string;
	job: string;
	type: string;
	monthlyDiscount?: string;
	practitioner?: string;
	client?: string;
	amount?: string;
	conflicts?: string;
} => {
	// Determine which conditional columns to show
	const hasAnyPractitioner = tableData.some((recipient) =>
		recipient.subRows.some((job) => job.job_practitioner_id)
	);
	const hasAnyClient = tableData.some(
		(recipient) => recipient.recipientType === RecipientTypeEnum.GUARANTOR
	);
	const hasAnyMonthlyDiscount = tableData.some((recipient) =>
		recipient.subRows.some((job) => job.is_ignore_monthly_discount)
	);
	const hasAnyPatient = tableData.some((recipient) =>
		recipient.subRows.some((job) => job.job_patient_id)
	);

	// More compact responsive widths
	const baseWidths = {
		date: "w-20 sm:w-24 md:w-28 lg:w-32 xl:w-36 2xl:w-40 3xl:w-44 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6", // Compact date column
		patient:
			"w-24 sm:w-28 md:w-32 lg:w-36 xl:w-40 2xl:w-44 3xl:w-48 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6", // Compact patient column
		job: "w-32 sm:w-40 md:w-48 lg:w-56 xl:w-64 2xl:w-72 3xl:w-80 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6", // Compact job column
		type: "w-16 sm:w-20 md:w-24 lg:w-28 xl:w-32 2xl:w-36 3xl:w-40 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6", // Compact type column
	};

	// Add conditional columns with compact responsive widths
	const conditionalWidths: Record<string, string> = {};

	if (hasAnyPatient) {
		conditionalWidths.patient =
			"w-24 sm:w-28 md:w-32 lg:w-36 xl:w-40 2xl:w-44 3xl:w-48 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6"; // Compact patient column
	}

	if (hasAnyPractitioner) {
		conditionalWidths.practitioner =
			"w-24 sm:w-28 md:w-32 lg:w-36 xl:w-40 2xl:w-44 3xl:w-48 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6"; // Compact practitioner column
	}

	if (hasAnyClient) {
		conditionalWidths.client =
			"w-24 sm:w-28 md:w-32 lg:w-36 xl:w-40 2xl:w-44 3xl:w-48 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6"; // Compact client column
	}

	if (hasAnyMonthlyDiscount) {
		conditionalWidths.monthlyDiscount =
			"w-12 sm:w-16 md:w-20 lg:w-24 xl:w-28 2xl:w-32 3xl:w-36 mr-1 sm:mr-2 md:mr-3 lg:mr-4 xl:mr-5 2xl:mr-6"; // Compact monthly discount column
	}

	return { ...baseWidths, ...conditionalWidths };
};

export const getAvailableCheckboxStates = (
	recipientRow: RecipientTableRow,
	dateRange: SelectionRange
): CheckboxState[] => {
	const allJobDocs = recipientRow.subRows;
	const jobDocsInDateRange = allJobDocs.filter((job) =>
		isInDateRange(job, dateRange)
	);

	const hasJobsInDateRange = jobDocsInDateRange.length > 0;
	const hasJobsOutsideDateRange =
		allJobDocs.length > jobDocsInDateRange.length;

	const availableStates = [CheckboxState.EMPTY];

	if (hasJobsInDateRange) {
		availableStates.push(CheckboxState.DATE_FILTERED);
	}

	// Only add FULL if it's different from DATE_FILTERED
	if (hasJobsOutsideDateRange && hasJobsInDateRange) {
		availableStates.push(CheckboxState.FULL);
	} else if (!hasJobsInDateRange && allJobDocs.length > 0) {
		// If no jobs in date range, go directly to FULL
		availableStates.push(CheckboxState.FULL);
	}

	return availableStates;
};

export const getNextCheckboxState = (
	currentState: CheckboxState,
	availableStates: CheckboxState[]
): CheckboxState => {
	const currentIndex = availableStates.indexOf(currentState);
	if (currentIndex === -1) {
		// If current state not found, default to first available state
		return availableStates[0];
	}
	const nextIndex = (currentIndex + 1) % availableStates.length;
	return availableStates[nextIndex];
};

/**
 * Checks if there are any conflicts across all recipients in the table data
 */
export const hasAnyConflictsInData = (
	tableData: RecipientTableRow[],
	selectedJobDocumentIds: Record<string, Set<number>>,
	getSelectedJobsConflicts?: (recipientKey: string) => string[]
): boolean => {
	if (!getSelectedJobsConflicts) return false;

	return tableData.some((recipient) => {
		const recipientKey = `${recipient.clientId}_${recipient.recipientId}`;
		const conflicts = getSelectedJobsConflicts(recipientKey);
		return conflicts.length > 0;
	});
};

export interface GlobalTotals {
	totalJobCount: number;
	totalGrossAmount: number;
	totalNetAmount: number;
	totalVatAmount: number;
	selectedJobCount: number;
	selectedGrossAmount: number;
	selectedNetAmount: number;
	selectedVatAmount: number;
	selectedAkontoDeduction: number;
}

export interface CurrencyTotals extends GlobalTotals {
	currency: string;
}

/**
 * Computes totals bucketed by the job currency (job_currency_text, defaulting to
 * CHF). Amounts in different currencies can't be summed, so the footer renders one
 * total per currency when a mix of currencies is present. Akonto belongs to the
 * recipient, so its deduction is attributed to the currency of that recipient's
 * selected jobs (exact in the normal single-currency case).
 */
export const calculateTotalsByCurrency = (
	recipientGroups: Array<{
		recipientType: RecipientTypeEnum;
		recipientId: string;
		clientId: string;
		jobDocuments: CompletedJobDocumentType[];
	}>,
	selectedJobDocumentIds: Record<string, Set<number>>,
	akontoLookup: Record<string, number>,
	akontoEnabled: Record<string, boolean>
): CurrencyTotals[] => {
	const byCurrency = new Map<string, CurrencyTotals>();

	const getBucket = (currency: string): CurrencyTotals => {
		let bucket = byCurrency.get(currency);
		if (!bucket) {
			bucket = {
				currency,
				totalJobCount: 0,
				totalGrossAmount: 0,
				totalNetAmount: 0,
				totalVatAmount: 0,
				selectedJobCount: 0,
				selectedGrossAmount: 0,
				selectedNetAmount: 0,
				selectedVatAmount: 0,
				selectedAkontoDeduction: 0,
			};
			byCurrency.set(currency, bucket);
		}
		return bucket;
	};

	recipientGroups.forEach((group) => {
		const recipientKey = `${group.clientId}_${group.recipientId}`;
		const akontoKey =
			group.recipientType === RecipientTypeEnum.GUARANTOR
				? `guarantor:${group.recipientId}`
				: `client:${group.recipientId}`;

		const selectedIds =
			selectedJobDocumentIds[recipientKey] || new Set<number>();
		const isAkontoEnabled = akontoEnabled[recipientKey] ?? true;
		const availableAkonto = akontoLookup[akontoKey] ?? 0;

		const recipientSelectedGrossByCurrency = new Map<string, number>();

		group.jobDocuments.forEach((job) => {
			const currency = job.job_currency_text || Currency.CHF;
			const bucket = getBucket(currency);
			const total = job.amount || 0;
			const taxRate = job.job_tax_rate || 0;
			const subtotal = total / (1 + taxRate / 100);
			const vatAmount = total - subtotal;

			bucket.totalJobCount++;
			bucket.totalGrossAmount += total;
			bucket.totalNetAmount += total;
			bucket.totalVatAmount += vatAmount;

			if (selectedIds.has(job.id as number)) {
				bucket.selectedJobCount++;
				bucket.selectedGrossAmount += total;
				bucket.selectedVatAmount += vatAmount;
				recipientSelectedGrossByCurrency.set(
					currency,
					(recipientSelectedGrossByCurrency.get(currency) ?? 0) +
						total
				);
			}
		});

		if (isAkontoEnabled) {
			let remainingAkonto = availableAkonto;
			recipientSelectedGrossByCurrency.forEach((gross, currency) => {
				if (remainingAkonto <= 0) return;
				const akontoToUse = Math.min(remainingAkonto, gross);
				getBucket(currency).selectedAkontoDeduction += akontoToUse;
				remainingAkonto -= akontoToUse;
			});
		}
	});

	byCurrency.forEach((bucket) => {
		bucket.selectedNetAmount =
			bucket.selectedGrossAmount - bucket.selectedAkontoDeduction;
	});

	return Array.from(byCurrency.values()).sort((a, b) =>
		a.currency.localeCompare(b.currency)
	);
};
